import React, { useState, useEffect } from "react";
import PageHeader from "../../componentes/PageHeader";
import { useTranslation } from "react-i18next";
import axios from "axios";
import GradientBanner from "../../componentes/Banner";
import {
  faFileAlt,
  faShieldAlt,
  faScroll,
  faBook,
  faClipboardList,
  faFilePdf,
  faKey,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { API_BASE_URL } from '../../api/config';

const Card = ({
  icon,
  title,
  description,
  subtitle1,
  subtitle1desc,
  subtitle2,
  bulletPoints,
  onDownloadClick,
  delay,
  downloadText,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{
      y: -8,
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    }}
    className="bg-white p-6 rounded-xl shadow-md transition-all duration-300 border-l-4 border-primary overflow-hidden group relative"
  >
    <div className="absolute -right-10 -top-10 w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full"></div>

    <div className="flex items-start relative z-10">
      <div className="bg-primary p-3 rounded-lg text-white transform transition-transform group-hover:rotate-6 group-hover:scale-110 flex-shrink-0">
        <FontAwesomeIcon icon={icon} className="text-xl" />
      </div>

      <div className="ml-4 flex-1 ">
        <h3 className="text-lg font-medium text-gray-800 mb-2 group-hover:text-primary transition-colors font-nunito-sans">
          {title}
        </h3>
        <p className="text-gray-700 leading-relaxed mb-3 font-nunito-sans">
          {description}
        </p>
        <div className="mb-3">
          <h4 className="text-lg font-medium text-gray-800 mb-1 font-nunito-sans">
            {subtitle1}
          </h4>
          <p className="text-gray-700 leading-relaxed font-nunito-sans">
            {subtitle1desc}
          </p>
        </div>
        <div className="mb-4">
          <h4 className="text-lg font-medium text-gray-800 mb-1 font-nunito-sans">
            {subtitle2}
          </h4>
          <ul className="space-y-1">
            {bulletPoints.map((point, index) => (
              <li
                key={index}
                className="flex items-center text-gray-700 font-nunito-sans"
              >
                <span className="w-1 h-1 bg-primary rounded-full mr-2"></span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
        <button
          onClick={onDownloadClick}
          className="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors font-medium font-nunito-sans"
        >
          <FontAwesomeIcon icon={faDownload} className="mr-2" />
          {downloadText}
        </button>
        <motion.div
          initial={{ opacity: 0, width: 0 }}
          whileHover={{ opacity: 1, width: "100%" }}
          className="h-0.5 bg-primary/20 mt-3"
        />
      </div>
    </div>
  </motion.div>
);

const RFC2350 = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [isSmallDevice, setIsSmallDevice] = useState(false);

  const introductionSection = [
    {
      title: t("rfc.toptitle"),
      description: t("rfc.topsubtitle"),
    },
  ];

  const [files, setFiles] = useState({
    rfc2350: {
      exists: false,
      error: null,
      contentType: null,
      filename: "RFC2350",
    },
    pgp: { exists: false, error: null, contentType: null, filename: "PGPKey" },
  });

  const fileUrls = {
    rfc2350: `${API_BASE_URL}/api/rfc2350/view/`,
    pgp: `${API_BASE_URL}/api/rfc2350/pgp/view/`,
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallDevice(window.innerWidth < 1280);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    const checkFileExists = async (fileKey, url) => {
      try {
        const headResponse = await axios.head(url, {
          timeout: 15000,
          validateStatus: (status) => status === 200,
        });

        const contentType = headResponse.headers["content-type"].split(";")[0];
        const filename =
          headResponse.headers["content-disposition"]?.match(
            /filename="(.+)"/
          )?.[1] || files[fileKey].filename;

        setFiles((prev) => ({
          ...prev,
          [fileKey]: {
            exists: true,
            error: null,
            contentType,
            filename,
          },
        }));
      } catch (error) {
        console.error(`${fileKey.toUpperCase()} Check Error:`, error);
        setFiles((prev) => ({
          ...prev,
          [fileKey]: {
            ...prev[fileKey],
            exists: false,
            error:
              error.message || `Unable to load ${fileKey.toUpperCase()} file`,
          },
        }));
      }
    };

    const checkAllFiles = async () => {
      const promises = Object.entries(files).map(([key]) =>
        checkFileExists(key, fileUrls[key])
      );

      await Promise.all(promises);
      setLoading(false);
    };

    checkAllFiles();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleDownload = (fileKey) => () => {
    if (!files[fileKey]?.exists) return;

    const { filename } = files[fileKey];
    const url = fileUrls[fileKey];

    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const cardsData = [
    {
      icon: faFilePdf,
      title: "RFC 2350",
      description: t("rfc.description"),
      subtitle1: t("rfc.subtitle1"),
      subtitle1desc: t("rfc.subtitle1desc"),
      subtitle2: t("rfc.subtitle2"),
      bulletPoints: [
        t("rfc.subtitle2desc1"),
        t("rfc.subtitle2desc2"),
        t("rfc.subtitle2desc3"),
      ],
      onDownloadClick: handleDownload("rfc2350"),
      downloadText: t("common.download"),
    },
    {
      icon: faKey,
      title: t("pgp.title"),
      description: t("pgp.description1"),
      subtitle1: t("pgp.subtitle1"),
      subtitle1desc: t("pgp.subtitle1desc"),
      subtitle2: t("pgp.subtitle2"),
      bulletPoints: [
        t("pgp.subtitle2desc1"),
        t("pgp.subtitle2desc2"),
        t("pgp.subtitle2desc3"),
      ],
      onDownloadClick: handleDownload("pgp"),
      downloadText: t("common.download"),
    },
  ];

  const renderSelectionButtons = () => (
    <div className="flex flex-col  px-6  md:px-12  lg:px-16 ">
      <div className="mb-10 ">
        {introductionSection.map((introduction, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="border-l-4 border-primary bg-white p-6 rounded-md shadow-sm transition-all duration-300"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-4 font-nunito-sans">
              {introduction.title}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed font-nunito-sans">
              {introduction.description}
            </p>
          </motion.div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        {cardsData.map((card, index) => (
          <Card
            key={index}
            icon={card.icon}
            title={card.title}
            description={card.description}
            subtitle1={card.subtitle1}
            subtitle1desc={card.subtitle1desc}
            subtitle2={card.subtitle2}
            bulletPoints={card.bulletPoints}
            onDownloadClick={card.onDownloadClick}
            delay={index * 0.1}
            downloadText={card.downloadText}
          />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("header.rfc_2350")}
          description={t("header.select_and_read_the_document")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("header.rfc_2350"), link: null },
          ]}
          backgroundIcons={[
            faFileAlt,
            faShieldAlt,
            faScroll,
            faBook,
            faClipboardList,
          ]}
        />
        <div className="flex justify-center items-center py-16">
          <FullscreenSpinner className="py-0" />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t("rfc2350.title")} />
      <GradientBanner
        title={t("header.rfc_2350")}
        description={t("header.select_and_read_the_document")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.rfc_2350"), link: null },
        ]}
        backgroundIcons={[
          faFileAlt,
          faShieldAlt,
          faScroll,
          faBook,
          faClipboardList,
        ]}
      />
      <div className="bg-gray-50 py-12">
        {renderSelectionButtons()}
      </div>
    </>
  );
};

export default RFC2350;